// src/lib/sms-validator.js — Éditeur SMS : VALIDATEUR pur, partagé client+serveur.
//
// ⚠️ SOURCE UNIQUE (pas un miroir) : ce module est du CJS pur (zéro import,
// zéro React, zéro Supabase) consommé :
//   - côté CLIENT par la vue SMS admin (compteur live + erreurs inline) —
//     CRA/webpack gère le CJS dans src/ ;
//   - côté SERVEUR via le shim `lib/sms-validator.js` (re-export require) —
//     la re-validation au save (`sms_template_save`) passe par LUI, jamais
//     confiance au client.
// Pourquoi le canonique vit dans src/ et pas lib/ : CRA (ModuleScopePlugin)
// INTERDIT l'import de la racine lib/ depuis src/ (cf. src/lib/sms-catalog.js),
// alors que le require inverse lib/ → src/ est légal (Node + trace Vercel nft).
// Un miroir dupliqué (pattern sms-catalog) serait un risque de drift sur un
// module de VALIDATION — inacceptable ici.
//
// Règles portées (décision Dimitri « même liberté d'édition que les emails ») :
//   (a) GSM-7 basique STRICT : caractères hors alphabet détectés et LISTÉS avec
//       suggestion de remplacement (é è à ù ì ò ä ö ü ñ OK ; ê â î ô û ë ï ç →
//       signalés). Non-GSM-7 = REFUS (décision : pas de bascule UCS-2
//       silencieuse — un seul caractère UCS-2 diviserait le budget par ~2).
//       Les caractères d'EXTENSION GSM-7 (€ [ ] { } ~ ^ | \) restent GSM-7
//       (pas d'UCS-2) mais coûtent 2 septets — comptés, pas refusés.
//   (b) Budget 160 en PIRE CAS réel : chaque variable développée à sa longueur
//       max (registre SMS_VAR_MAX_LEN). > 160 septets = AVERTISSEMENT explicite
//       (« 2 segments, coût double ») — pas un refus.
//   (c) MENTION de désinscription OBLIGATOIRE sur les gabarits marketing
//       (refus sans elle — regex tolérante ; le texte n'a plus qu'un lien, la
//       désinscription passe par le bouton de la page de destination) ;
//       heuristique promo (% / promo / code) sur les transactionnels =
//       avertissement (pas refus).
//   (d) Whitelist de variables PAR gabarit : toute variable inconnue = REFUS,
//       accolades non appariées/vides = REFUS. Le save est la SEULE barrière :
//       au runtime SMS il n'existe AUCUN filet de variables (l'interpolation
//       {\w+} remplace le connu — vide → '' — et laisse tout le reste EN
//       CLAIR dans le SMS envoyé, comme pour les textes codés).

// ── Alphabet GSM-7 (03.38) ───────────────────────────────────────────────────
// Table de base (1 septet par caractère). NB : Ç majuscule y est, ç minuscule
// NON (piège classique) ; à minuscule y est, À majuscule NON.
const GSM7_BASIC_CHARS =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ" +
  " !\"#¤%&'()*+,-./0123456789:;<=>?" +
  "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑܧ" +
  "¿abcdefghijklmnopqrstuvwxyzäöñüà";
// Table d'extension (2 septets : ESC + caractère). \f = form feed (rarissime).
const GSM7_EXTENDED_CHARS = "\f^{}\\[~]|€";

const GSM7_BASIC_SET = new Set(GSM7_BASIC_CHARS.split(""));
const GSM7_EXTENDED_SET = new Set(GSM7_EXTENDED_CHARS.split(""));

// Suggestions de remplacement pour les caractères français/typographiques
// courants HORS alphabet. Fallback générique : décomposition NFD sans
// diacritiques si le résultat est GSM-7, sinon null (pas de suggestion).
const GSM7_SUGGESTIONS = {
  ê: "e",
  â: "a",
  î: "i",
  ô: "o",
  û: "u",
  ë: "e",
  ï: "i",
  ç: "c",
  Ê: "E",
  Â: "A",
  Î: "I",
  Ô: "O",
  Û: "U",
  Ë: "E",
  Ï: "I",
  À: "A",
  È: "E",
  Ì: "I",
  Ò: "O",
  Ù: "U",
  œ: "oe",
  Œ: "OE",
  ÿ: "y",
  "’": "'",
  "‘": "'",
  "“": '"',
  "”": '"',
  "«": '"',
  "»": '"',
  "–": "-",
  "—": "-",
  "…": "...",
  " ": " ", // espace insécable
  " ": " ", // espace fine insécable
};

// ── Bornes segments ──────────────────────────────────────────────────────────
// 1 segment GSM-7 = 160 septets ; au-delà, concaténation : 153 septets utiles
// par segment (7 octets d'en-tête UDH).
const SMS_SEGMENT_MAX = 160;
const SMS_MULTI_SEGMENT_SIZE = 153;

// Borne dure anti-abus du corps BRUT (avant expansion) — un gabarit SMS n'a
// aucune raison d'approcher ça (4 segments pleins). Refus au-delà.
const MAX_SMS_BODY_LEN = 640;

// ── Registre PIRE CAS par variable ───────────────────────────────────────────
// Longueur MAX de chaque variable une fois développée à l'envoi :
//   - lien      : lien court parcours /s/<12 hex> = 44 car RÉELS sur la base
//                 prod https://sejour.fullbnbzen.com (SMS_LINK_BUDGET,
//                 lib/sms-templates.js). NB : la consigne disait « liens courts
//                 40 » mais le lien parcours réel fait 44 — le pire cas doit
//                 être le VRAI pire cas (44 couvre aussi le /r/<8> marketing).
//   - lien_stop : lien court désinscription /r/<code8> = 40 car.
//   - code      : code promo nominatif, 17 car max (cf. dédup promo-codes).
//   - prenom    : borné 30 car (registre décision éditeur SMS).
//   - logement  : 30 car (record réel connu : 28, « Villa Belle Vue Terrasse
//                 Sud » — marge zéro documentée pessimist #962).
//   - code_promo: alias de {code} (17 car) — MÊME borne, cf. dédup promo-codes.
//
// ★ B5 (QA adversariale LOT 3) — {logement} RESTE WHITELISTÉ, VOLONTAIREMENT.
// La première version de ce lot le RETIRAIT du registre ET des whitelists.
// Conséquence non vue : `handleSmsTemplateSave` (lib/sms-templates-handlers.js)
// RE-VALIDE le corps FR à CHAQUE save. Toute ligne `sms_templates` contenant
// encore {logement} — état DB inconnu, le pré-check SQL n'a jamais été lancé —
// serait retournée en 400 `variable_inconnue` au prochain save : l'admin ne
// pourrait PLUS enregistrer ce gabarit (et l'auto-traduction EN/ES au save
// tomberait avec). Critère retenu : AUCUN gabarit existant ne doit devenir
// non-enregistrable ⇒ on garde {logement} ACCEPTÉ.
// Le souci de budget qui motivait le retrait est traité par un AVERTISSEMENT
// explicite (code `variable_logement_deconseillee`) + l'avertissement budget
// `depassement_160` existant : le registre RESTE à 30 c, donc le pire cas est
// compté JUSTE (le retirer du registre en le laissant whitelisté ferait
// retomber expandWorstCase sur son défaut de 20 c = sous-estimation).
// Rappel D4 : le lien reste 44 c (domaine sejour.fullbnbzen.com FIGÉ) — la
// variable d'ajustement d'un SMS trop long est LE TEXTE.
// Retrait définitif possible plus tard, APRÈS la migration de nettoyage
// supabase/migrations/20261102000002_sms_templates_logement_cleanup.sql
// (fournie NON appliquée) + un pré-check DB nul.
const SMS_VAR_MAX_LEN = {
  prenom: 30,
  logement: 30,
  lien: 44,
  lien_stop: 40,
  code: 17,
  code_promo: 17,
};

// Variables DÉCONSEILLÉES (acceptées au save, mais signalées) : elles ne sont
// plus interpolées par les textes codés et coûtent cher en budget segment.
// AVERTISSEMENT — jamais un refus (cf. ★ B5 ci-dessus).
const SMS_DISCOURAGED_VARS = new Set(["logement"]);

// ── Règles PAR GABARIT (les 5 slugs éditables) ───────────────────────────────
// kind : 'transactionnel' (parcours Direct, lib/sms-templates.js — JAMAIS de
// contenu promo, pas de lien stop possible) | 'marketing' (post-séjour OTA,
// lib/marketing-sms.js — MENTION de désinscription OBLIGATOIRE, L.34-5 CPCE).
// vars : whitelist STRICTE des variables autorisées dans le corps.
// varMax : override PAR GABARIT du registre (le {lien} marketing est un
// /r/<code8> = 40 car, pas le /s/<12 hex> = 44 du parcours Direct).
//
// ★ B5 — {logement} CONSERVÉ dans les 4 transactionnels (cf. bloc ★ B5 du
// registre ci-dessus) : le retirer rendrait NON-ENREGISTRABLE tout gabarit
// déjà stocké qui le contient. Il n'est plus interpolé par les textes codés
// 2026-07-21 (lib/sms-templates.js) mais reste rendu correctement au runtime
// (`logement` est toujours passé dans les vars par lib/journey-comms.js et
// lib/direct-journey-sweep.js). Son coût est signalé par l'avertissement
// `variable_logement_deconseillee`.
//
// ★ REFONTE 2026-07-27 — le jeu passe de 5 à 4 gabarits (décision Dimitri) :
// validation_lien / rappel_caution_j2 / confirmation_livret sont RETIRÉS ;
// instructions_arrivee (transactionnel) et sms_post_sejour_direct (marketing)
// sont AJOUTÉS. Sur les 2 NOUVEAUX slugs, {logement} n'est PAS whitelisté :
// l'argument ★ B5 (« ne pas rendre non-enregistrable un corps déjà stocké »)
// ne s'applique qu'aux slugs PRÉ-EXISTANTS — un slug neuf n'a aucune ligne en
// base, on part donc sur la whitelist STRICTE (seules les variables réellement
// interpolées au runtime). rappel_caution_j0, lui, le conserve (ligne existante).
const SMS_TEMPLATE_RULES = {
  rappel_caution_j0: {
    kind: "transactionnel",
    vars: ["prenom", "logement", "lien"],
  },
  // « SMS 2 » — instructions d'arrivée, la veille, dossier complet.
  // Whitelist STRICTE : lib/direct-journey-sweep.js n'interpole que
  // {prenom}/{logement}/{lien} ; {logement} n'est pas utilisé par le texte codé
  // et n'a aucune ligne en base à préserver → non whitelisté ici.
  instructions_arrivee: {
    kind: "transactionnel",
    vars: ["prenom", "lien"],
  },
  // « SMS 3b » — après-séjour DIRECT, sans promo. kind:'marketing' VOLONTAIRE :
  // le corps porte une mention de désinscription et invite à re-réserver → la
  // règle la plus stricte s'applique (mention OBLIGATOIRE, refus sans elle).
  // Pas de {code} : le direct n'a PAS de promo (décision Dimitri).
  sms_post_sejour_direct: {
    kind: "marketing",
    vars: ["prenom", "lien"],
    varMax: { lien: 40 }, // lien court /r/<code8>, comme le 3a
  },
  sms_post_sejour_promo: {
    kind: "marketing",
    // {prenom} ajouté (texte 2026-07-21 personnalisé) ; {code} + {lien}
    // (lien UNIQUE vers /sejour-ota, qui porte le bouton « Se désinscrire »).
    // {lien_stop} RESTE autorisé (rétro-compat / édition libre) mais n'est
    // plus OBLIGATOIRE : la désinscription est désormais garantie par une
    // MENTION dans le corps + le bouton sur la page de destination (décision
    // Dimitri 2026-07-21 ; L.34-5 — à confirmer avocat).
    //
    // ⛔ LOT 3 — {code_promo} N'EST PAS whitelisté ici, VOLONTAIREMENT : le
    // runtime marketing (lib/marketing-sms.js::renderPostStaySms) interpole
    // {prenom}/{code}/{lien}/{lien_stop} et N'EXPOSE PAS la clé `code_promo`.
    // Un {code_promo} whitelisté mais non rendu PARTIRAIT EN CLAIR dans le SMS
    // (le validateur au save est la SEULE barrière). Le SMS 3a garde donc
    // {code}. Pour l'autoriser un jour : d'abord aliaser `code_promo: code`
    // dans marketing-sms.js, ENSUITE seulement l'ajouter ici.
    vars: ["prenom", "code", "lien", "lien_stop"],
    varMax: { lien: 40 }, // lien merci marketing = /r/<code8> (40), pas /s/<12>
  },
};

// ── Conditions autorisées dans les blocs {#if COND}…{/if} (LOT 1) ────────────
// SÉMANTIQUE MIROIR du moteur email (lib/template-variables.js /
// src/lib/template-variables.js) : un bloc {#if COND}…{/if} est CONSERVÉ ssi
// resolveValue(COND, ctx) renvoie une chaîne NON-VIDE (après trim), SUPPRIMÉ
// sinon. Le validateur ne dispose PAS d'un ctx réel : il énumère à la place
// TOUTES les combinaisons présent/absent de chaque condition (produit cartésien
// borné) et valide GSM-7 + budget sur CHAQUE variante rendue.
//
// La whitelist ci-dessous n'est PAS inventée : elle liste EXACTEMENT les
// variables « booléennes » (retour "true"/"") que resolveValue sait dériver et
// qui ont un sens en condition d'affichage adaptatif. Chaque entrée renvoie à
// son `case` dans template-variables.js :
//   is_airbnb / is_booking / is_direct   → source de la résa (case :335-340)
//   caution_required                     → source∈{booking,direct} (case :341)
//   payment_received / caution_received  → timestamps résa (case :379-382)
//   deposit_link_visible                 → paiement + source (case :383)
//   welcome_checkin_allowed              → airbnb || caution reçue (case :388)
//   caution_authorized / caution_captured→ caution_status Stripe (case :400-401)
//   caution_manquante_effective          → matrice-aware (D2/A-8, LOT 2)
//   caution_required_effective           → matrice-aware (D2/A-8, LOT 2)
//   formulaire_manquant / formulaire_rempli → ctx.formulaire_rempli (case :427-431)
//
// ★ B6 (QA adversariale LOT 3) — DETTE PRÉ-EXISTANTE À CONNAÎTRE, ET RAISON
// POUR LAQUELLE reservation_complete / reservation_incomplete NE SONT PAS
// AJOUTÉES ICI : les deux moteurs qui rendent RÉELLEMENT ces corps
// (lib/sms-templates.js::interpolate pour le parcours Direct,
// lib/marketing-sms.js::renderPostStaySms pour le SMS 3a) ne font qu'un
// `replace(/\{(\w+)\}/g, …)` et N'ÉVALUENT AUCUN bloc {#if}. Les balises
// `{#if X}` / `{/if}` ne matchent pas `\w+` → elles sont laissées LITTÉRALES
// et PARTIRAIENT EN CLAIR dans le SMS du voyageur.
// ⇒ Whitelister une condition ici revient à AUTORISER AU SAVE un corps que le
// runtime ne sait pas rendre. Les 13 conditions ci-dessous portent déjà ce
// défaut (PRÉ-EXISTANT, hors scope de ce lot — à traiter dans un lot dédié :
// soit implémenter l'évaluation {#if} côté SMS, soit vider cette whitelist).
// Le LOT 3 refuse d'AGGRAVER la dette : le prédicat « dossier complet » vit
// dans les `conditions` de message_sends (où il coupe l'ENVOI entier, ce qu'un
// {#if} ne fait pas — cf. section (b) de la PR), pas dans un {#if} SMS.
// Une condition {#if X} avec X ∉ cette whitelist ⇒ erreur `condition_inconnue`
// (le moteur email la laisserait passer mais rendrait le bloc TOUJOURS supprimé
//  — piège silencieux : mieux vaut REFUSER au save).
//
// D2 (décision Dimitri) — les LITTÉRAUX caution_manquante / caution_required
// sont BANNIS des conditions SMS : ils ignorent la matrice
// journey_composition_config → un override « caution OFF » ne couperait PAS le
// rappel (LOT 2 court-circuité, SMS « déposez la caution » indu = chemin argent).
// Seules les variables matrice-aware caution_manquante_effective /
// caution_required_effective sont autorisées ici. Les littéraux restent réservés
// aux gabarits EMAIL OTA (dormance F-M1) — hors de ce whitelist SMS.
const SMS_CONDITION_VARS = new Set([
  "is_airbnb",
  "is_booking",
  "is_direct",
  "caution_required_effective",
  "payment_received",
  "caution_received",
  "deposit_link_visible",
  "welcome_checkin_allowed",
  "caution_authorized",
  "caution_captured",
  "caution_manquante_effective",
  "formulaire_manquant",
  "formulaire_rempli",
]);

// Groupes de conditions MUTUELLEMENT EXCLUSIVES : au plus UNE peut être vraie
// à l'envoi. Sert à NE PAS générer les branches impossibles du produit
// cartésien (sinon SUR-REJET : une branche « airbnb ET booking » afficherait
// deux textes plateforme jamais concomitants → pire cas gonflé artificiellement).
//   - plateforme : une résa a exactement UNE source (is_airbnb/is_booking/is_direct).
//   - formulaire : formulaire_manquant = ¬formulaire_rempli (négation exacte).
// (★ B6 — pas de groupe « dossier » : reservation_complete /
//  reservation_incomplete ne sont PAS whitelistées côté SMS, cf. supra.)
// On applique la contrainte « au plus 1 active » (on N'exige PAS « exactement 1 » :
// la branche où AUCUNE n'est active est plus COURTE, donc sans effet sur le pire
// cas — on la laisse pour rester conservateur). Les couples caution_manquante /
// caution_received ne sont PAS déclarés exclusifs (dérivations distinctes ;
// sur-compter n'entraîne qu'un AVERTISSEMENT budget, jamais un refus).
const SMS_CONDITION_EXCLUSIVE_GROUPS = [
  ["is_airbnb", "is_booking", "is_direct"],
  ["formulaire_manquant", "formulaire_rempli"],
];

// Borne anti-explosion combinatoire : n = nombre de conditions DISTINCTES
// utilisées dans le corps (des blocs répétés avec la MÊME condition basculent
// ensemble → comptent pour 1). n ≤ 4 ⇒ au plus 2⁴ = 16 variantes énumérées
// (moins après filtrage d'exclusivité). Au-delà ⇒ erreur `trop_de_conditions`.
const MAX_IF_CONDITIONS = 4;

// ── LOT 5 — PSEUDO-SLUG « send SMS » (message_sends canal='sms') ─────────────
//
// LE GAP QUE CE BLOC FERME : SMS_TEMPLATE_RULES est keyé par les 5 slugs LEGACY
// de `sms_templates` (parcours Direct + marketing OTA). Un corps de
// `message_sends` canal='sms' — dont le TEXTE vit dans `messages.body_fr` et
// dont le rendu passe par lib/messages-engine.js — n'a AUCUN de ces slugs :
// avant ce lot, il n'était couvert par AUCUNE whitelist, AUCUN budget 160,
// AUCUN contrôle GSM-7. Un tel send pouvait donc partir en UCS-2, sur 4
// segments, ou avec un token non résolu affiché EN CLAIR au voyageur (le
// moteur ne « rattrape » que les identifiants STRICTS {[a-zA-Z_]\w*} via
// findMissingVars → skipped_missing_vars ; un token non-identifiant comme
// {prénom}, { lien_sejour } ou {lien\nstop} part LITTÉRALEMENT).
//
// ⚠️ REGISTRE SÉPARÉ, JAMAIS DANS `SMS_TEMPLATE_RULES` : y ajouter un 6e slug
// casserait (i) src/lib/sms-catalog-sync.test.js (catalogue = clés = CHECK DB
// = seed), (ii) lib/sms-templates-handlers.js (upsert dans `sms_templates`
// dont le CHECK n'autorise que 5 slugs → 500), (iii) la vue SMS
// (SMS_CATALOG.map). Le pseudo-slug n'est PAS un slug de `sms_templates` :
// `sms_template_save` doit continuer de le refuser en `slug_inconnu`.
const SMS_SEND_PSEUDO_SLUG = "message_send_sms";

// Whitelist de VARIABLES d'un corps de send SMS. Contrainte dure : toute
// variable whitelistée ici DOIT être résolue par le moteur (lib/template-
// variables.js) — sinon findMissingVars SKIPPE l'envoi en silence (SMS jamais
// parti, aucune alerte admin).
//
// ★ FUSION LOT 3 (#1093) × LOT 5 (#1094) — {code_promo} RESTE HORS DE CETTE
// WHITELIST : LA RÈGLE LA PLUS STRICTE GAGNE.
// Les deux lots portaient chacun une whitelist de variables SMS. Le LOT 3 a
// rendu {code_promo} RÉSOLVABLE par le moteur (case dans template-variables.js
// + mint JIT `getOrRefreshPromoCode` dans lib/messages-engine.js) — le motif
// historique de son absence ici (« aucun `case "code_promo"` n'existe ») N'EST
// DONC PLUS VRAI. Il n'est pas rétabli pour autant, pour trois raisons :
//   1. LE LOT 3 L'A LUI-MÊME REFUSÉ CÔTÉ MARKETING — SMS_TEMPLATE_RULES
//      .sms_post_sejour_promo garde {code}, pas {code_promo} (cf. bloc ⛔ plus haut).
//      Ouvrir la porte côté sends SMS contredirait cet arbitrage.
//   2. {code_promo} traîne DES GARDES DE SEND (lib/code-promo-policy.js :
//      whitelist de canaux hors tchat_ota, gate opt-out marketing, refus de
//      mint avant fin de séjour). Un send SMS qui en porterait un serait
//      SKIPPÉ au runtime dès qu'une garde s'arme — le validateur au save
//      afficherait « vert » un envoi structurellement non délivrable.
//   3. C'est une SOLLICITATION COMMERCIALE : l'écran « configurateur d'envois
//      Plateformes » de ce lot est transactionnel (kind:'transactionnel'), et
//      le SMS promo post-séjour OTA RESTE dans lib/marketing-sms.js (décision
//      D2, inchangée).
// Pour l'autoriser un jour : câbler d'abord les 3 gardes de code-promo-policy
// sur le chemin `sms_send_save`, PUIS l'ajouter ici — jamais l'inverse.
//
// ⚠️ LONGUEUR DU LIEN — 77, PAS 44 (point d'arbitrage à remonter) :
// sur CE chemin, le moteur injecte `buildSejourOtaUrl(token)` =
// `https://sejour.fullbnbzen.com/sejour-ota/<UUID>` = 29 + 12 + 36 = 77 car.
// Les 44 car de la décision D4 sont ceux du lien COURT `/s/<12 hex>` du
// parcours Direct (SMS_LINK_BUDGET, lib/sms-templates.js) — le raccourcisseur
// existe (lib/short-links.js) mais SEUL lib/marketing-sms.js s'en sert.
// Enregistrer 44 pendant que le moteur injecte 77 produirait un validateur
// MENTEUR (vert à l'écran, 2 segments à l'envoi) : ici « le pire cas doit
// être le VRAI pire cas ». Le jour où un lot MOTEUR raccourcit {lien_sejour}
// pour canal='sms', cette ligne passe de 77 à 44 (et le test
// sms-validator-send.test.js « registre ≥ URL réelle » le garde honnête).
const SMS_SEND_VAR_MAX_LEN = {
  guest_first_name: 30, // borné comme {prenom} (registre éditeur SMS)
  lien_sejour: 77, // /sejour-ota/<UUID> — voir ci-dessus
};

// Conditions {#if …} autorisées dans un corps de send SMS. Sous-ensemble
// STRICT de SMS_CONDITION_VARS :
//   - is_booking / is_airbnb : les 2 SEULES plateformes d'un send Plateformes
//     (ADR-266 — `is_direct` est BANNI ici : jamais de branche Direct dans un
//     envoi Plateformes) ;
//   - caution_manquante_effective : matrice-aware (D2 — les littéraux
//     caution_manquante / caution_required restent bannis) ;
//   - formulaire_manquant : drapeau formulaire de bienvenue.
// Avec 4 conditions, le pire cas énuméré reste sous MAX_IF_CONDITIONS.
const SMS_SEND_CONDITION_VARS = new Set([
  "is_airbnb",
  "is_booking",
  "caution_manquante_effective",
  "formulaire_manquant",
]);

const SMS_SEND_RULES = {
  kind: "transactionnel",
  vars: ["guest_first_name", "lien_sejour"],
  varMax: SMS_SEND_VAR_MAX_LEN,
  conditions: SMS_SEND_CONDITION_VARS,
};

/**
 * Résolution UNIQUE des règles par slug : les 5 gabarits `sms_templates` OU le
 * pseudo-slug des sends SMS. Byte-identique à l'ancien `SMS_TEMPLATE_RULES[slug]`
 * pour les 5 slugs existants (aucune régression possible).
 * @returns {object|null}
 */
function smsRulesForSlug(slug) {
  if (Object.prototype.hasOwnProperty.call(SMS_TEMPLATE_RULES, slug)) {
    return SMS_TEMPLATE_RULES[slug];
  }
  return slug === SMS_SEND_PSEUDO_SLUG ? SMS_SEND_RULES : null;
}

// Whitelist de conditions applicable à un slug (défaut = whitelist historique).
function _condVarsForSlug(slug) {
  const rules = smsRulesForSlug(slug);
  return (rules && rules.conditions) || SMS_CONDITION_VARS;
}

// ── Mention de désinscription (marketing) ────────────────────────────────────
// Le corps marketing DOIT contenir une mention de désinscription (L.34-5 CPCE :
// expéditeur alphanumérique, aucune réponse STOP possible → le moyen de
// désinscription = mention dans le SMS + bouton « Se désinscrire » sur la page
// de destination /sejour-ota). Regex TOLÉRANTE (accents optionnels, EN inclus).
// SOURCE UNIQUE : réutilisée au runtime par lib/marketing-sms.js (filet L.34-5).
// NON-globale (usage .test() répété — pas de lastIndex).
const DESINSCRIPTION_MENTION_RE =
  /désinscription|desinscription|désabonn|desabonn|stop|unsubscribe|se désinscrire|se desinscrire/i;

// Heuristique promo SIMPLE (décision mission) : % / promo / code. Sur un
// gabarit transactionnel → AVERTISSEMENT (pas refus) : un contenu promotionnel
// sur le canal transactionnel serait une infraction (pas de STOP possible).
const PROMO_HEURISTIC_RE = /%|promo|code/i;

// ── Primitives GSM-7 ─────────────────────────────────────────────────────────

/**
 * Liste les caractères HORS alphabet GSM-7 (base + extension) d'un texte.
 * @returns {Array<{char:string, count:number, suggestion:string|null}>}
 *   uniques, ordre de première apparition.
 */
function findNonGsm7Chars(text) {
  if (typeof text !== "string") return [];
  const seen = new Map();
  for (const ch of text) {
    if (GSM7_BASIC_SET.has(ch) || GSM7_EXTENDED_SET.has(ch)) continue;
    const cur = seen.get(ch);
    if (cur) {
      cur.count++;
      continue;
    }
    let suggestion = null;
    if (Object.prototype.hasOwnProperty.call(GSM7_SUGGESTIONS, ch)) {
      suggestion = GSM7_SUGGESTIONS[ch];
    } else {
      // Fallback générique : décomposition sans diacritiques si GSM-7.
      const stripped = ch.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      if (
        stripped !== ch &&
        stripped.length > 0 &&
        findNonGsm7CharsRawOk(stripped)
      ) {
        suggestion = stripped;
      }
    }
    seen.set(ch, { char: ch, count: 1, suggestion });
  }
  return [...seen.values()];
}

// Vrai si TOUS les caractères du texte sont GSM-7 (base ou extension).
function findNonGsm7CharsRawOk(text) {
  for (const ch of text) {
    if (!GSM7_BASIC_SET.has(ch) && !GSM7_EXTENDED_SET.has(ch)) return false;
  }
  return true;
}

/**
 * Longueur d'un texte en SEPTETS GSM-7 (extension = 2 septets), ou null si le
 * texte contient au moins un caractère non-GSM-7 (le compte n'a pas de sens :
 * l'envoi basculerait UCS-2 — refusé en amont).
 */
function gsm7Septets(text) {
  if (typeof text !== "string") return null;
  let n = 0;
  for (const ch of text) {
    if (GSM7_BASIC_SET.has(ch)) n += 1;
    else if (GSM7_EXTENDED_SET.has(ch)) n += 2;
    else return null;
  }
  return n;
}

// Nombre de segments pour n septets (n ≤ 160 → 1 ; sinon 153/segment).
function segmentsForSeptets(n) {
  if (typeof n !== "number" || n <= 0) return n === 0 ? 0 : null;
  if (n <= SMS_SEGMENT_MAX) return 1;
  return Math.ceil(n / SMS_MULTI_SEGMENT_SIZE);
}

// ── Variables ────────────────────────────────────────────────────────────────

/**
 * Capture LARGE des {tokens} (durcie vs l'éditeur Direct) : tout `{…}` est
 * candidat, Y COMPRIS multi-ligne (`{lien\nstop}` — évasion fermée : \n n'est
 * PLUS une sortie de capture) et les formes non-\w ({prénom}, { lien }) que
 * le moteur d'interpolation (regex \w+) laisserait EN CLAIR dans le SMS.
 * Comparaison BRUTE (pas de trim) à la whitelist.
 * @returns {string[]} tokens inconnus uniques (ordre d'apparition)
 */
function findUnknownVars(slug, body) {
  const rules = smsRulesForSlug(slug);
  const declared = new Set(rules ? rules.vars : []);
  const found = [];
  const seen = new Set();
  if (typeof body !== "string") return found;
  const re = /\{([^{}]+)\}/g;
  let m;
  while ((m = re.exec(body)) !== null) {
    const tok = m[1];
    if (!declared.has(tok) && !seen.has(tok)) {
      seen.add(tok);
      found.push(tok);
    }
  }
  return found;
}

/**
 * Vrai si le corps contient des accolades NON APPARIÉES ou VIDES (`{` orphelin,
 * `}` orphelin, `{}`). Refusées au save : elles échappent à la capture de
 * tokens et partiraient telles quelles dans le SMS (surface d'évasion).
 */
function hasUnpairedBraces(body) {
  if (typeof body !== "string") return false;
  const residue = body.replace(/\{[^{}]+\}/g, "");
  return /[{}]/.test(residue);
}

/**
 * Développe le corps en PIRE CAS : chaque variable whitelistée remplacée par
 * 'X' × longueur max du registre (X = 1 septet GSM-7 — neutre pour le compte
 * ET pour la détection non-GSM-7 : seuls les caractères TAPÉS par l'admin
 * ressortent). Les tokens inconnus restent tels quels (refusés par ailleurs).
 */
function expandWorstCase(slug, body) {
  const rules = smsRulesForSlug(slug);
  if (typeof body !== "string") return "";
  if (!rules) return body;
  return body.replace(/\{([^}\n]+)\}/g, (m, key) => {
    if (rules.vars.includes(key)) {
      const len =
        rules.varMax && typeof rules.varMax[key] === "number"
          ? rules.varMax[key]
          : SMS_VAR_MAX_LEN[key];
      return "X".repeat(typeof len === "number" ? len : 20);
    }
    return m;
  });
}

// ── Blocs conditionnels {#if COND}…{/if} (LOT 1) ─────────────────────────────
//
// MIROIR EXACT de IF_BLOCK_RE (lib/template-variables.js:449) : même motif, même
// sémantique (bloc conservé ssi condition vraie, PAS de nesting, multi-ligne).
// Dupliqué et NON importé car ModuleScopePlugin interdit à src/ d'importer la
// racine lib/ (cf. en-tête de ce fichier). Le moteur email ne supporte NI {#else}
// NI l'imbrication — on reproduit strictement ce contrat. Toute évolution de la
// syntaxe des blocs côté moteur email DOIT être répercutée ici.
// #1048 (cumul ET/OU) : la capture d'expression accepte désormais les espaces
// (comme IF_BLOCK_RE côté moteur email : [a-zA-Z0-9_ \t]*) → {#if A ET B} /
// {#if A OU B} sont RECONNUS comme blocs (avant : rejetés en blocs_mal_apparies,
// finding QA #1048). L'expression est ensuite parsée par parseIfExpression.
const SMS_IF_BLOCK_RE = /\{#if\s+([a-zA-Z_][a-zA-Z0-9_ \t]*)\}([\s\S]*?)\{\/if\}/g;

// ── Cumul de conditions ET / OU (#1048, MIROIR evalIfExpression du moteur email
// lib/template-variables.js) ─────────────────────────────────────────────────
// Une expression de bloc {#if EXPR} est soit une variable unique, soit un cumul
// HOMOGÈNE `var (ET var)+` (conjonction) / `var (OU var)+` (disjonction). UN
// SEUL type d'opérateur par bloc ; un mélange ET/OU, un opérateur en tête/fin,
// une expression vide → structure NON SUPPORTÉE. Casse STRICTE (ET/OU
// majuscules) : mêmes règles que le moteur email (byte-identité single-var).
//
// @returns {{ vars:string[], op:'SINGLE'|'ET'|'OU'|null, valid:boolean }}
function parseIfExpression(expr) {
  const tokens = String(expr).trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return { vars: [], op: null, valid: false };
  const isVar = (t) => /^[a-zA-Z_]\w*$/.test(t);
  if (tokens.length === 1) {
    return isVar(tokens[0])
      ? { vars: [tokens[0]], op: "SINGLE", valid: true }
      : { vars: [], op: null, valid: false };
  }
  // Structure attendue : var (OP var)+ → longueur IMPAIRE.
  if (tokens.length % 2 === 0) return { vars: [], op: null, valid: false };
  const vars = [];
  const ops = [];
  for (let i = 0; i < tokens.length; i++) {
    (i % 2 === 0 ? vars : ops).push(tokens[i]);
  }
  if (!vars.every(isVar)) return { vars: [], op: null, valid: false };
  const allEt = ops.every((o) => o === "ET");
  const allOu = ops.every((o) => o === "OU");
  if (!allEt && !allOu) return { vars: [], op: null, valid: false };
  return { vars, op: allEt ? "ET" : "OU", valid: true };
}

// Évalue une expression parsée contre l'ensemble des VARIABLES actives d'une
// branche (produit cartésien). MÊME sémantique de vérité que le moteur email :
// une variable est « vraie » ssi présente dans activeVars. Expression invalide
// → false (bloc masqué, fail-safe — comme le moteur email).
function evalIfExprForBranch(parsed, activeVars) {
  if (!parsed || !parsed.valid) return false;
  const truthy = (v) => activeVars.has(v);
  if (parsed.op === "ET") return parsed.vars.every(truthy);
  if (parsed.op === "OU") return parsed.vars.some(truthy);
  return truthy(parsed.vars[0]); // SINGLE
}

/**
 * Analyse la STRUCTURE des blocs {#if}…{/if} d'un corps (appariement, nesting,
 * conditions connues, borne). N'ÉVALUE rien — repère seulement les conditions
 * distinctes et les erreurs structurelles bloquantes.
 *
 * @returns {{
 *   conditions: string[],      // conditions DISTINCTES, ordre d'apparition
 *   branchable: boolean,       // true = énumération du cartésien sûre
 *   structuralError: boolean,
 *   error: {code:string, message:string, [k:string]:any}|null
 * }}
 * Codes d'erreur : blocs_mal_apparies · condition_malformee (#1048, cumul ET/OU
 *   mal formé) · condition_inconnue · trop_de_conditions.
 *
 * @param {string} body
 * @param {Set<string>} [allowedConditions] — LOT 5 : whitelist de conditions
 *   applicable. OPTIONNEL, défaut = SMS_CONDITION_VARS → comportement
 *   HISTORIQUE byte-identique pour les 5 gabarits. Le pseudo-slug des sends SMS
 *   passe SMS_SEND_CONDITION_VARS (sous-ensemble strict, sans is_direct).
 */
function analyzeIfBlocks(body, allowedConditions) {
  const allowed =
    allowedConditions instanceof Set ? allowedConditions : SMS_CONDITION_VARS;
  const result = {
    conditions: [],
    branchable: true,
    structuralError: false,
    error: null,
  };
  if (typeof body !== "string") return result;
  // Court-circuit : aucun marqueur de bloc → rien à analyser (chemin nominal
  // des gabarits sans conditionnel, byte-identique à l'existant).
  if (body.indexOf("{#if") === -1 && body.indexOf("{/if}") === -1) {
    return result;
  }

  const seen = new Set();
  const unknownConds = [];
  let depth = 0;
  let malformed = false;
  let malformedExpr = false; // #1048 : expression ET/OU mal formée (mélange, etc.)
  let nested = false;
  let orphanClose = false;

  // Tokens de contrôle, dans l'ordre. Alternatives ORDONNÉES (JS teste de
  // gauche à droite à chaque position) :
  //   1. open VALIDE `{#if EXPR}`  (EXPR = identifiant OU cumul ET/OU, #1048)
  //   2. close VALIDE `{/if}`
  //   3. open MALFORMÉ `{#if…` (ex. `{#if}`, `{#if 2x}`)
  //   4. close MALFORMÉ `{ /if…` / `{/if …`
  const TOKEN_RE =
    /\{#if\s+([a-zA-Z_][a-zA-Z0-9_ \t]*)\}|\{\/if\}|\{#if\b|\{\s*\/if\b/g;
  let m;
  while ((m = TOKEN_RE.exec(body)) !== null) {
    if (m[1] !== undefined) {
      // open valide (structurellement) — parse l'EXPRESSION (single-var OU cumul)
      if (depth > 0) nested = true; // pas de nesting (contrat moteur email)
      depth++;
      const parsed = parseIfExpression(m[1]);
      if (!parsed.valid) {
        // Expression reconnue comme bloc mais mal formée (mélange ET/OU,
        // opérateur en tête/fin…) : au runtime le moteur email masquerait le
        // bloc en silence → on REFUSE au save (piège silencieux).
        malformedExpr = true;
        continue;
      }
      // Chaque VARIABLE de l'expression = un AXE du produit cartésien.
      for (const cond of parsed.vars) {
        if (!seen.has(cond)) {
          seen.add(cond);
          result.conditions.push(cond); // NB: `conditions` = VARIABLES distinctes
        }
        if (!allowed.has(cond) && !unknownConds.includes(cond)) {
          unknownConds.push(cond);
        }
      }
    } else if (m[0] === "{/if}") {
      // close valide
      if (depth === 0) orphanClose = true;
      else depth--;
    } else {
      // open ou close MALFORMÉ
      malformed = true;
    }
  }

  if (malformedExpr) {
    result.structuralError = true;
    result.branchable = false;
    result.error = {
      code: "condition_malformee",
      message:
        "Paragraphe conditionnel mal écrit : utilisez soit que des ET, " +
        "soit que des OU, jamais les deux mélangés.",
    };
    return result;
  }

  if (malformed || nested || orphanClose || depth !== 0) {
    result.structuralError = true;
    result.branchable = false;
    let why;
    if (malformed) why = "un bloc {#if …} ou {/if} est mal écrit";
    else if (nested) why = "les blocs {#if} sont imbriqués (non supporté)";
    else if (orphanClose) why = "un {/if} n'a pas de {#if} ouvrant";
    else why = "un {#if} n'est jamais fermé par {/if}";
    result.error = {
      code: "blocs_mal_apparies",
      message:
        `Un paragraphe conditionnel est mal fermé : ${why}. ` +
        "Chaque paragraphe s'ouvre et se ferme une fois, sans en mettre un " +
        "dans un autre.",
    };
    return result;
  }

  if (unknownConds.length > 0) {
    const list = unknownConds.map((c) => `{#if ${c}}`).join(", ");
    const allowedList = [...allowed].join(", ");
    result.structuralError = true;
    result.branchable = false;
    result.error = {
      code: "condition_inconnue",
      conditions: unknownConds,
      message:
        `Condition(s) inconnue(s) : ${list}. ` +
        `Conditions autorisées : ${allowedList}.`,
    };
    return result;
  }

  if (result.conditions.length > MAX_IF_CONDITIONS) {
    result.structuralError = true;
    result.branchable = false;
    result.error = {
      code: "trop_de_conditions",
      count: result.conditions.length,
      max: MAX_IF_CONDITIONS,
      message:
        `Trop de conditions distinctes (${result.conditions.length} > ` +
        `${MAX_IF_CONDITIONS}) : impossible de vérifier le budget sur toutes ` +
        `les variantes (2^n). Réduis le nombre de blocs {#if} distincts.`,
    };
    return result;
  }

  return result;
}

/**
 * Rend UNE variante : pour chaque bloc {#if EXPR}…{/if}, garde le contenu ssi
 * l'EXPRESSION (single-var OU cumul ET/OU, #1048) est vraie pour l'ensemble des
 * VARIABLES actives `activeVars`, le supprime sinon. MÊME sémantique que
 * evaluateConditions/evalIfExpression (moteur email) ; variables NON encore
 * développées (elles le seront par expandWorstCase).
 */
function renderIfBranch(body, activeVars) {
  if (typeof body !== "string") return "";
  return body.replace(SMS_IF_BLOCK_RE, (mtch, expr, content) =>
    evalIfExprForBranch(parseIfExpression(expr), activeVars) ? content : "",
  );
}

/**
 * Énumère les variantes (produit cartésien présent/absent) des conditions,
 * en écartant les combinaisons IMPOSSIBLES (groupes mutuellement exclusifs :
 * au plus 1 condition active par groupe).
 * @param {string[]} conditions — distinctes, ordonnées
 * @returns {Set<string>[]} — chaque Set = conditions ACTIVES de la variante
 */
function enumerateConditionBranches(conditions) {
  const n = conditions.length;
  if (n === 0) return [new Set()];
  const branches = [];
  for (let mask = 0; mask < 1 << n; mask++) {
    const active = new Set();
    for (let i = 0; i < n; i++) {
      if (mask & (1 << i)) active.add(conditions[i]);
    }
    let possible = true;
    for (const grp of SMS_CONDITION_EXCLUSIVE_GROUPS) {
      let cnt = 0;
      for (const c of grp) if (active.has(c)) cnt++;
      if (cnt > 1) {
        possible = false;
        break;
      }
    }
    if (possible) branches.push(active);
  }
  return branches;
}

/**
 * Agrège la validation GSM-7 / whitelist / budget sur TOUTES les variantes.
 * @returns {{
 *   gsm7: boolean,                 // false si ≥1 variante non-GSM-7
 *   maxLength: number|null,        // septets de la variante la PLUS LONGUE (null si !gsm7)
 *   maxSegments: number|null,
 *   worstLabel: string[]|null,     // conditions actives de la variante la plus longue
 *   badChars: Array,               // union des caractères non-GSM-7 (toutes variantes)
 *   unknownVars: string[],         // union des variables inconnues (toutes variantes)
 *   branchCount: number
 * }}
 */
function budgetAcrossBranches(slug, body, conditions, branches) {
  const badMap = new Map();
  const unknownSet = new Set();
  const unknownOrder = [];
  let maxLength = null;
  let worstLabel = null;

  for (const active of branches) {
    const branchBody = renderIfBranch(body, active);
    for (const tok of findUnknownVars(slug, branchBody)) {
      if (!unknownSet.has(tok)) {
        unknownSet.add(tok);
        unknownOrder.push(tok);
      }
    }
    const expanded = expandWorstCase(slug, branchBody);
    const bad = findNonGsm7Chars(expanded);
    for (const b of bad) {
      const cur = badMap.get(b.char);
      if (cur) cur.count += b.count;
      else
        badMap.set(b.char, {
          char: b.char,
          count: b.count,
          suggestion: b.suggestion,
        });
    }
    if (bad.length === 0) {
      const len = gsm7Septets(expanded);
      if (maxLength === null || len > maxLength) {
        maxLength = len;
        worstLabel = conditions.filter((c) => active.has(c));
      }
    }
  }

  const gsm7 = badMap.size === 0;
  return {
    gsm7,
    maxLength: gsm7 ? maxLength : null,
    maxSegments:
      gsm7 && maxLength !== null ? segmentsForSeptets(maxLength) : null,
    worstLabel: gsm7 ? worstLabel : null,
    badChars: [...badMap.values()],
    unknownVars: unknownOrder,
    branchCount: branches.length,
  };
}

// Décrit les conditions actives d'une variante pour l'affichage
// (« la branche [is_booking + caution_manquante] »).
function describeBranch(labelArr) {
  return Array.isArray(labelArr) && labelArr.length
    ? labelArr.join(" + ")
    : "aucune condition active";
}

/**
 * Compteur live (UI) : pire cas d'un corps pour un slug.
 *
 * Rétro-compat STRICTE : un corps SANS bloc {#if} exploitable renvoie EXACTEMENT
 * `{ gsm7, length, segments }` (comportement historique, byte-identique).
 * Un corps AVEC blocs {#if} (multi-branches) enrichit le retour de `branchCount`
 * et `worstBranch` ({conditions, length, segments}) — le pire cas est la variante
 * la PLUS LONGUE (l'UI affiche « pire cas N/160 sur la branche … »).
 *
 * @returns {{ gsm7:boolean, length:number|null, segments:number|null,
 *   branchCount?:number, worstBranch?:{conditions:string[],length:number|null,segments:number|null}|null }}
 */
function computeWorstCase(slug, body) {
  const ifInfo = analyzeIfBlocks(body, _condVarsForSlug(slug));
  // Pas de blocs exploitables (aucun {#if}, ou structure invalide) → mono-branche
  // historique. Pour une structure invalide, l'erreur est signalée par
  // validateSmsTemplate ; ici on rend un compteur best-effort sur le corps brut.
  if (ifInfo.conditions.length === 0 || ifInfo.structuralError) {
    const expanded = expandWorstCase(slug, body);
    const length = gsm7Septets(expanded);
    return {
      gsm7: length !== null,
      length,
      segments: length === null ? null : segmentsForSeptets(length),
    };
  }
  const branches = enumerateConditionBranches(ifInfo.conditions);
  const b = budgetAcrossBranches(slug, body, ifInfo.conditions, branches);
  return {
    gsm7: b.gsm7,
    length: b.maxLength,
    segments: b.maxSegments,
    branchCount: b.branchCount,
    worstBranch: b.gsm7
      ? {
          conditions: b.worstLabel || [],
          length: b.maxLength,
          segments: b.maxSegments,
        }
      : null,
  };
}

// ── Validateur complet (contrat du save) ─────────────────────────────────────

/**
 * Valide un corps de gabarit SMS pour un slug donné.
 *
 * @param {string} slug — un des 5 slugs SMS_TEMPLATE_RULES.
 * @param {string} body — corps du gabarit (avec {variables} non développées).
 * @returns {{
 *   ok: boolean,                    // false = save REFUSÉ (au moins 1 error)
 *   errors: Array<{code:string, message:string}>,     // bloquantes
 *   warnings: Array<{code:string, message:string}>,   // informatives
 *   worstCase: {gsm7:boolean, length:number|null, segments:number|null}|null
 * }}
 * Codes d'erreur : slug_inconnu · corps_vide · corps_trop_long · non_gsm7
 *   (+ .chars [{char,count,suggestion}]) · variable_inconnue (+ .unknown []) ·
 *   accolades_non_appariees · mention_desinscription_manquante ·
 *   blocs_mal_apparies · condition_malformee (#1048) ·
 *   condition_inconnue (+ .conditions []) · trop_de_conditions (+ .count, .max).
 * Codes d'avertissement : depassement_160 (+ .length, .segments, .branch []) ·
 *   contenu_promo_transactionnel · variable_logement_deconseillee (+ .vars []).
 * worstCase multi-branches : + .branchCount, .worstBranch {conditions,length,segments}.
 * Blocs {#if COND}…{/if} (LOT 1) : whitelist SMS_CONDITION_VARS, produit
 *   cartésien borné (MAX_IF_CONDITIONS conditions distinctes), exclusivité
 *   plateforme/formulaire, erreurs = UNION, budget = variante la plus longue.
 */
function validateSmsTemplate(slug, body) {
  const errors = [];
  const warnings = [];

  const rules = smsRulesForSlug(slug);
  if (!rules) {
    return {
      ok: false,
      errors: [
        {
          code: "slug_inconnu",
          message: `Gabarit SMS inconnu : ${String(slug)}.`,
        },
      ],
      warnings: [],
      worstCase: null,
    };
  }

  if (typeof body !== "string" || body.trim() === "") {
    return {
      ok: false,
      errors: [
        {
          code: "corps_vide",
          message: "Le corps du SMS ne peut pas être vide.",
        },
      ],
      warnings: [],
      worstCase: null,
    };
  }

  if (body.length > MAX_SMS_BODY_LEN) {
    errors.push({
      code: "corps_trop_long",
      message: `Corps trop long (max ${MAX_SMS_BODY_LEN} caractères bruts).`,
    });
  }

  // (LOT 1) Analyse des blocs conditionnels {#if COND}…{/if}. Un corps SANS
  // {#if} → ifInfo.conditions=[] → chemin mono-branche historique (byte-identique).
  // Un corps AVEC blocs valides → produit cartésien borné, validation par branche.
  // Une structure invalide (mal appariée / condition inconnue / trop de blocs) →
  // erreur structurelle bloquante ; on N'énumère PAS (rendu non fiable).
  const ifInfo = analyzeIfBlocks(body, rules.conditions || SMS_CONDITION_VARS);
  if (ifInfo.structuralError) {
    errors.push(ifInfo.error);
  }

  const multiBranch = !ifInfo.structuralError && ifInfo.conditions.length > 0;
  // Budget/whitelist/GSM-7 par branche — calculé une seule fois (réutilisé plus bas).
  const branchAgg = multiBranch
    ? budgetAcrossBranches(
        slug,
        body,
        ifInfo.conditions,
        enumerateConditionBranches(ifInfo.conditions),
      )
    : null;

  // (d) Whitelist de variables PAR gabarit — variable inconnue = REFUS. Le
  // SAVE est la SEULE barrière : au runtime, l'interpolation ({\w+} →
  // valeur, vide → '') laisse tout token inconnu ou malformé EN CLAIR dans
  // le SMS envoyé — aucun filet d'envoi ne re-vérifie les variables.
  // Multi-branches : UNION des variables inconnues de toutes les variantes
  // (une var dans un bloc {#if} n'apparaît que dans les branches où le bloc
  // est présent — l'union les capture toutes). Structure invalide : on saute
  // (les {#if…} bruts seraient faussement comptés comme variables).
  const unknown = ifInfo.structuralError
    ? []
    : multiBranch
      ? branchAgg.unknownVars
      : findUnknownVars(slug, body);
  if (unknown.length > 0) {
    const list = unknown.map((t) => `{${t}}`).join(", ");
    const allowed = rules.vars.map((v) => `{${v}}`).join(", ");
    errors.push({
      code: "variable_inconnue",
      unknown,
      message:
        `Info inconnue pour ce SMS : ${list}. ` +
        `Infos autorisées : ${allowed}.`,
    });
  }

  // (d-bis) Accolades non appariées ou vides ({ orphelin, } orphelin, {}) :
  // REFUS — elles échappent à la capture de tokens et partiraient telles
  // quelles dans le SMS (fermeture de la surface d'évasion m1).
  if (hasUnpairedBraces(body)) {
    errors.push({
      code: "accolades_non_appariees",
      message:
        "Une accolade { ou } est seule ou vide dans le texte : elle " +
        "partirait telle quelle dans le SMS — refusé.",
    });
  }

  // (c) MENTION de désinscription OBLIGATOIRE sur les gabarits marketing
  // (L.34-5 CPCE : expéditeur alphanumérique, pas de STOP-réponse possible →
  // le moyen de désinscription = mention dans le corps + bouton « Se
  // désinscrire » sur la page de destination /sejour-ota, décision Dimitri
  // 2026-07-21, à confirmer avocat). REFUS si le corps ne contient AUCUNE
  // mention (DESINSCRIPTION_MENTION_RE). Remplace l'ancienne exigence du lien
  // {lien_stop} (le texte n'a plus qu'UN seul lien).
  //
  // LOT 1 — MULTI-BRANCHES : la mention doit être présente dans CHAQUE variante
  // rendue. Une mention cachée dans un {#if X} disparaîtrait des branches où X
  // est absent → un SMS marketing SANS mention pourrait partir (trou L.34-5).
  // On teste donc chaque branche ; mention manquante sur ≥1 branche = REFUS.
  if (rules.kind === "marketing") {
    let mentionMissing;
    if (multiBranch) {
      mentionMissing = enumerateConditionBranches(ifInfo.conditions).some(
        (active) =>
          !DESINSCRIPTION_MENTION_RE.test(renderIfBranch(body, active)),
      );
    } else {
      // Mono-branche ou structure invalide : test sur le corps brut (historique).
      mentionMissing = !DESINSCRIPTION_MENTION_RE.test(body);
    }
    if (mentionMissing) {
      errors.push({
        code: "mention_desinscription_manquante",
        message:
          "Gabarit marketing : une MENTION de désinscription est OBLIGATOIRE " +
          "dans le corps — et dans CHAQUE variante des blocs {#if} (ex. " +
          "« Désinscription via ce lien. » / « Unsubscribe via this link. ») — " +
          "sans elle, le SMS ne partirait jamais (L.34-5 : aucune réponse STOP " +
          "possible).",
      });
    }
  }

  // (d-ter) ★ B5 — variables DÉCONSEILLÉES : {logement} reste ACCEPTÉ (aucun
  // gabarit existant ne doit devenir non-enregistrable) mais son coût est
  // signalé. AVERTISSEMENT, jamais un refus. Le budget réel est, lui, contrôlé
  // par `depassement_160` (le registre garde bien logement = 30 c).
  const discouraged = [...SMS_DISCOURAGED_VARS].filter(
    (v) => typeof body === "string" && body.includes(`{${v}}`),
  );
  if (discouraged.length > 0) {
    warnings.push({
      code: "variable_logement_deconseillee",
      vars: discouraged,
      message:
        `Info déconseillée : ${discouraged.map((v) => `{${v}}`).join(", ")}. ` +
        "Elle peut ajouter jusqu'à 30 caractères et faire payer 2 SMS au " +
        "lieu d'un. Elle reste acceptée, mais mieux vaut s'en passer.",
    });
  }

  // (c-inverse) Contenu promo sur un TRANSACTIONNEL — heuristique simple
  // (% / promo / code) : AVERTISSEMENT, pas refus.
  if (rules.kind === "transactionnel" && PROMO_HEURISTIC_RE.test(body)) {
    warnings.push({
      code: "contenu_promo_transactionnel",
      message:
        "Ce gabarit est TRANSACTIONNEL : un contenu promotionnel (%, promo, " +
        "code) y est interdit (canal sans désinscription possible). Vérifiez " +
        "que ce n'est pas du marketing déguisé.",
    });
  }

  // (a) GSM-7 + (b) budget 160 en PIRE CAS. Non-GSM-7 = REFUS (pas de bascule
  // UCS-2 silencieuse) ; > 160 = AVERTISSEMENT (décision Dimitri ③ : JAMAIS un
  // refus, l'admin assume le coût). Les {variables} whitelistées deviennent des
  // 'X' neutres (expandWorstCase) → seuls les caractères tapés par l'admin
  // ressortent.
  let worstCase;
  if (ifInfo.structuralError) {
    // Structure invalide : rendu non fiable → pas de compteur (erreur déjà posée).
    worstCase = null;
  } else if (multiBranch) {
    // Multi-branches : erreurs = UNION (GSM-7/whitelist → REFUS sur toute
    // variante) ; budget = variante la PLUS LONGUE.
    if (branchAgg.badChars.length > 0) {
      const list = branchAgg.badChars
        .map(
          (c) =>
            `« ${c.char} »${c.suggestion !== null ? ` (remplacer par « ${c.suggestion} »)` : ""}`,
        )
        .join(", ");
      errors.push({
        code: "non_gsm7",
        chars: branchAgg.badChars,
        message:
          `Caractère(s) refusé(s) par les SMS : ${list}. ` +
          "Un seul suffirait à diviser par deux la place disponible — " +
          "retirez-le.",
      });
    }
    worstCase = {
      gsm7: branchAgg.gsm7,
      length: branchAgg.maxLength,
      segments: branchAgg.maxSegments,
      branchCount: branchAgg.branchCount,
      worstBranch: branchAgg.gsm7
        ? {
            conditions: branchAgg.worstLabel || [],
            length: branchAgg.maxLength,
            segments: branchAgg.maxSegments,
          }
        : null,
    };
    if (branchAgg.gsm7 && branchAgg.maxLength > SMS_SEGMENT_MAX) {
      const seg = branchAgg.maxSegments;
      const where = describeBranch(branchAgg.worstLabel);
      warnings.push({
        code: "depassement_160",
        length: branchAgg.maxLength,
        segments: seg,
        branch: branchAgg.worstLabel || [],
        message:
          `Dans le cas le plus long ([${where}]), ce SMS ferait ` +
          `${branchAgg.maxLength} caractères (plus de ${SMS_SEGMENT_MAX}) : il ` +
          `serait facturé comme ${seg} SMS — coût ${seg === 2 ? "double" : `×${seg}`}.`,
      });
    }
  } else {
    // Mono-branche (aucun {#if}) : chemin historique, byte-identique.
    const expanded = expandWorstCase(slug, body);
    const badChars = findNonGsm7Chars(expanded);
    if (badChars.length > 0) {
      const list = badChars
        .map(
          (c) =>
            `« ${c.char} »${c.suggestion !== null ? ` (remplacer par « ${c.suggestion} »)` : ""}`,
        )
        .join(", ");
      errors.push({
        code: "non_gsm7",
        chars: badChars,
        message:
          `Caractère(s) refusé(s) par les SMS : ${list}. ` +
          "Un seul suffirait à diviser par deux la place disponible — " +
          "retirez-le.",
      });
    }
    const length = badChars.length === 0 ? gsm7Septets(expanded) : null;
    worstCase = {
      gsm7: badChars.length === 0,
      length,
      segments: length === null ? null : segmentsForSeptets(length),
    };
    if (length !== null && length > SMS_SEGMENT_MAX) {
      const seg = segmentsForSeptets(length);
      warnings.push({
        code: "depassement_160",
        length,
        segments: seg,
        message:
          `Avec les infos les plus longues, ce SMS ferait ${length} ` +
          `caractères (plus de ${SMS_SEGMENT_MAX}) : il serait facturé comme ` +
          `${seg} SMS — coût ${seg === 2 ? "double" : `×${seg}`}.`,
      });
    }
  }

  return { ok: errors.length === 0, errors, warnings, worstCase };
}

// ── LOT 5 — API dédiée aux sends SMS (message_sends canal='sms') ─────────────

/**
 * Valide le corps d'un send SMS (le TEXTE vit dans `messages.body_*`, la ligne
 * `message_sends` ne porte que le déclencheur). Sucre sur validateSmsTemplate
 * avec le pseudo-slug — MÊME module côté client (confort inline) et côté
 * serveur (la vraie barrière, action `sms_send_save`).
 * @param {string} body
 * @returns {{ok:boolean, errors:Array, warnings:Array, worstCase:object|null}}
 */
function validateSmsSendBody(body) {
  return validateSmsTemplate(SMS_SEND_PSEUDO_SLUG, body);
}

/**
 * Plateformes ciblées par un tableau `message_sends.conditions`.
 * MIROIR (volontairement minimal) de deriveSendConditionState côté UI
 * (src/tabs/messages/MessageEditor.jsx) — ce module est du CJS pur consommable
 * par le SERVEUR, qui ne peut pas importer du JSX. Absorbe les DEUX formes
 * réellement présentes en base : `{source, in, [...]}` canonique et
 * `{source, eq, 'airbnb'}` legacy scalaire.
 * @param {Array} conditions
 * @returns {string[]} plateformes (ordre d'apparition, sans doublon)
 */
function smsSendPlatformsFromConditions(conditions) {
  const out = [];
  const list = Array.isArray(conditions) ? conditions : [];
  for (const c of list) {
    if (!c || c.field !== "reservation.source") continue;
    const vals =
      c.op === "in" && Array.isArray(c.value)
        ? c.value
        : c.op === "eq" && typeof c.value === "string"
          ? [c.value]
          : [];
    for (const v of vals) {
      if (typeof v === "string" && v && !out.includes(v)) out.push(v);
    }
  }
  return out;
}

/**
 * GARDE ÉDITORIALE (c) — « bloc caution + plateforme Airbnb » INTERDIT.
 *
 * Fondement code : `matrixCautionOn` renvoie `false` EN DUR pour airbnb
 * (« plancher dur AirCover », lib/journey-composition-resolver.js) ⇒ sur une
 * résa Airbnb le bloc {#if caution_manquante_effective} serait SILENCIEUSEMENT
 * supprimé — et si TOUT le texte tient dans ce bloc, le SMS partirait VIDE.
 * La garde est donc à la fois éditoriale (Airbnb n'a jamais de caution) et
 * anti-SMS-vide. BLOQUANTE côté UI ET côté serveur (400).
 *
 * ⚠️ FAIL-CLOSED SUR « AUCUNE PLATEFORME COCHÉE » (blocker QA #1094-B1) :
 * quand l'admin ne coche AUCUNE plateforme, `buildSendConditions`
 * (src/tabs/messages/MessageEditor.jsx) n'émet AUCUNE condition
 * `reservation.source` — et l'UI l'annonce elle-même : « rien coché = toutes
 * les réservations Plateformes ». Un tel send MATCHE donc les résas Airbnb.
 * Tester `platforms.includes('airbnb')` seul rendait la garde CONTOURNABLE :
 * il suffisait de ne rien cocher pour qu'un envoi 100 % caution passe l'UI ET
 * le serveur, puis parte VIDE sur Airbnb (matrixCautionOn=false ⇒ bloc
 * supprimé). Un tableau vide (ou absent) est donc traité comme
 * « inclut Airbnb ».
 *
 * @param {{body:string, platforms:string[]}} p
 * @returns {'airbnb_coche'|'aucune_plateforme'|null} motif du conflit (null = OK)
 */
function smsSendCautionAirbnbConflictReason({ body, platforms } = {}) {
  const plats = Array.isArray(platforms)
    ? platforms.filter((p) => typeof p === "string" && p)
    : [];
  // [] ⇒ « toutes les plateformes », Airbnb compris (cf. bloc ci-dessus).
  const reason = plats.length === 0 ? "aucune_plateforme" : null;
  if (reason === null && !plats.includes("airbnb")) return null;
  const info = analyzeIfBlocks(body, SMS_SEND_CONDITION_VARS);
  if (!info.conditions.includes("caution_manquante_effective")) return null;
  return reason || "airbnb_coche";
}

function smsSendCautionAirbnbConflict(p) {
  return smsSendCautionAirbnbConflictReason(p) !== null;
}

const SMS_SEND_CAUTION_AIRBNB_MESSAGE =
  "Airbnb n'a jamais de caution (AirCover) : le bloc « caution » serait " +
  "supprimé pour toute réservation Airbnb (et le SMS pourrait partir vide). " +
  "Retirez la plateforme Airbnb, ou retirez le bloc caution du texte.";

const SMS_SEND_CAUTION_NO_PLATFORM_MESSAGE =
  "Aucune plateforme n'est cochée : l'envoi ciblerait TOUTES les réservations " +
  "Plateformes, Airbnb compris — or Airbnb n'a jamais de caution (AirCover), " +
  "le bloc « caution » y serait supprimé et le SMS pourrait partir vide. " +
  "Cochez explicitement Booking, ou retirez le bloc caution du texte.";

/** Message d'erreur correspondant au motif renvoyé par …ConflictReason. */
function smsSendCautionAirbnbMessage(reason) {
  return reason === "aucune_plateforme"
    ? SMS_SEND_CAUTION_NO_PLATFORM_MESSAGE
    : SMS_SEND_CAUTION_AIRBNB_MESSAGE;
}

// ── DERNIER REMPART MOTEUR — corps SMS *RENDU* (blocker QA #1094-B1c/B4) ─────
//
// POURQUOI CE FILET EXISTE MALGRÉ LA BARRIÈRE DE SAVE : le TEXTE d'un send SMS
// vit dans `messages.body_fr`, et la table `messages` conserve une policy
// `admin_all … FOR ALL TO authenticated` — un admin peut donc réécrire le corps
// depuis la console du navigateur SANS repasser par `sms_send_save` (UCS-2,
// 4 segments, token littéral). Fermer cette policy est hors scope de ce lot ;
// on valide donc AUSSI au moment de l'ENVOI, sur le corps RÉELLEMENT rendu.
//
// ⚠️ CALIBRAGE VOLONTAIRE — ce filet reproduit EXACTEMENT les REFUS du save,
// jamais ses simples AVERTISSEMENTS : un corps de 161-640 caractères (2-5
// segments) a pu être accepté sciemment par l'admin (l'écran l'affiche en
// ambre). Le refuser ici transformerait un envoi assumé en non-envoi
// silencieux — exactement le mal qu'on veut éviter. Sont refusés :
//   • corps vide / blanc  ← le cas « tout le texte dans un {#if} supprimé » ;
//   • caractère hors GSM-7 (bascule UCS-2, budget divisé par ~2) ;
//   • accolade résiduelle : un token non-identifiant ({prénom}, { lien_sejour })
//     échappe à findMissingVars et partirait LITTÉRALEMENT au voyageur ;
//   • dépassement de la borne dure MAX_SMS_BODY_LEN (= refus `corps_trop_long`).
//
// Toute accolade est refusée : `{` et `}` appartiennent à l'extension GSM-7
// mais aucun SMS FBZ légitime n'en contient — leur présence après rendu
// signale un token non résolu ou un bloc conditionnel mal formé.
const RENDERED_BRACE_RE = /\{[^{}]*\}|[{}]/;

/**
 * Valide un corps SMS DÉJÀ RENDU (variables substituées, {#if} évalués).
 * @param {string} body
 * @returns {{ok:boolean, reasons:Array<{code:string,message:string}>,
 *            length:number|null, segments:number|null}}
 */
function validateRenderedSmsBody(body) {
  const text = typeof body === "string" ? body : "";
  const reasons = [];

  if (text.trim() === "") {
    return {
      ok: false,
      reasons: [
        {
          code: "corps_vide_rendu",
          message:
            "Le SMS rendu est vide : tout le texte tenait probablement dans un " +
            "bloc {#if} faux pour cette réservation (ex. caution sur Airbnb).",
        },
      ],
      length: null,
      segments: null,
    };
  }

  const bad = findNonGsm7Chars(text);
  if (bad.length > 0) {
    reasons.push({
      code: "non_gsm7_rendu",
      message:
        "Caractère(s) hors GSM-7 dans le SMS rendu : " +
        bad.map((c) => `« ${c.char} »`).join(", ") +
        " — le SMS basculerait en UCS-2 (budget divisé par ~2).",
    });
  }

  const leftover = text.match(RENDERED_BRACE_RE);
  if (leftover) {
    reasons.push({
      code: "token_non_resolu",
      message:
        "Accolade résiduelle dans le SMS rendu : " +
        `« ${String(leftover[0]).slice(0, 40)} » — elle partirait ` +
        "LITTÉRALEMENT au voyageur.",
    });
  }

  const septets = gsm7Septets(text);
  const segments = septets === null ? null : segmentsForSeptets(septets);
  if (septets !== null && septets > MAX_SMS_BODY_LEN) {
    reasons.push({
      code: "corps_trop_long_rendu",
      message:
        `SMS rendu de ${septets} caractères (max ${MAX_SMS_BODY_LEN}) — ` +
        `${segments} segments.`,
    });
  }

  return { ok: reasons.length === 0, reasons, length: septets, segments };
}

module.exports = {
  GSM7_BASIC_CHARS,
  GSM7_EXTENDED_CHARS,
  GSM7_SUGGESTIONS,
  SMS_SEGMENT_MAX,
  SMS_MULTI_SEGMENT_SIZE,
  MAX_SMS_BODY_LEN,
  SMS_VAR_MAX_LEN,
  SMS_DISCOURAGED_VARS,
  SMS_TEMPLATE_RULES,
  SMS_CONDITION_VARS,
  SMS_CONDITION_EXCLUSIVE_GROUPS,
  MAX_IF_CONDITIONS,
  DESINSCRIPTION_MENTION_RE,
  findNonGsm7Chars,
  gsm7Septets,
  segmentsForSeptets,
  findUnknownVars,
  hasUnpairedBraces,
  expandWorstCase,
  computeWorstCase,
  validateSmsTemplate,
  // LOT 1 — blocs conditionnels {#if}…{/if} (exposés pour tests + réutilisation).
  analyzeIfBlocks,
  renderIfBranch,
  enumerateConditionBranches,
  budgetAcrossBranches,
  // #1048 — cumul ET/OU (exposés pour tests + parité moteur email).
  parseIfExpression,
  evalIfExprForBranch,
  // LOT 5 — sends SMS (message_sends canal='sms') : registre SÉPARÉ + API.
  SMS_SEND_PSEUDO_SLUG,
  SMS_SEND_RULES,
  SMS_SEND_VAR_MAX_LEN,
  SMS_SEND_CONDITION_VARS,
  SMS_SEND_CAUTION_AIRBNB_MESSAGE,
  SMS_SEND_CAUTION_NO_PLATFORM_MESSAGE,
  smsSendCautionAirbnbMessage,
  smsRulesForSlug,
  validateSmsSendBody,
  smsSendPlatformsFromConditions,
  smsSendCautionAirbnbConflict,
  smsSendCautionAirbnbConflictReason,
  // Dernier rempart moteur — corps SMS RENDU (contourne la policy admin_all).
  validateRenderedSmsBody,
};
